/** @jsxImportSource preact */
import * as m from "../../paraglide/messages.js";
import { AlertIcon, CheckIcon, ChevronDownIcon } from "../icons";
import type { WidgetTokens } from "../theme";
import { Field, Select } from "./base-ui.mjs";
import { FORM_STYLES, formVars } from "./form-styles";

export interface SelectOption {
  /** 选项的文字，已经是买家看到的语言。 */
  label: string;
  value: string;
}

export interface SelectFieldProps {
  tokens: WidgetTokens;
  label: string;
  options: SelectOption[];
  name?: string;
  /** 当前选中的值；没有值时触发器显示 placeholder。 */
  value?: string;
  defaultValue?: string;
  onValueChange?: (value: string) => void;
  placeholder?: string;
  /** 字段下方的提示。 */
  description?: string;
  /** 错误文字；有它就算无效，并替换提示。 */
  error?: string;
  invalid?: boolean;
  disabled?: boolean;
  /** 受控地展开或收起列表；设计稿的静态展示用它把列表固定展开。 */
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
}

/**
 * 下拉框的样式。触发器是内凹井（同输入框：聚焦或展开变白底加墨色环，无效是危险色环）；
 * 列表是白色大圆角浮层带柔阴影，条目是胶囊：悬停井色，选中墨黑实心加对勾。
 * 列表不进 Portal，留在 Shadow DOM 里，不会跑到店面页面上；定位层由 Base UI 绝对定位，盖在下方字段上面，不占文档流。
 * 至少和触发器一样宽、左边对齐；下方放不下时翻到上方，两边都不够就按可用高度收起、列表内部滚动。
 */
export const SELECT_FIELD_STYLES = `
.gn-select-trigger { box-sizing:border-box; display:flex; align-items:center; justify-content:space-between; gap:8px; width:100%; height:44px; padding:0 14px; border:0; border-radius:var(--gn-form-r-input); background:var(--gn-form-sheet); color:var(--gn-form-ink); font:inherit; font-size:14px; text-align:left; cursor:pointer; transition:background .15s, box-shadow .15s; }
.gn-select-trigger:focus-visible, .gn-select-trigger[data-popup-open] { outline:none; background:var(--gn-form-sheet); box-shadow:0 0 0 2px var(--gn-form-ring); }
.gn-select-trigger[data-invalid] { box-shadow:0 0 0 1.5px var(--gn-form-danger); }
.gn-select-trigger[data-disabled] { opacity:.55; cursor:not-allowed; }
.gn-select-value { flex:1 1 0; min-width:0; overflow:hidden; text-overflow:ellipsis; white-space:nowrap; }
.gn-select-value[data-placeholder] { color:var(--gn-form-faint); }
.gn-select-icon { display:flex; flex:none; color:var(--gn-form-quiet); transition:transform .15s; }
.gn-select-trigger[data-popup-open] .gn-select-icon { transform:rotate(180deg); }
.gn-select-positioner { z-index:10; min-width:var(--anchor-width); max-width:var(--available-width); }
.gn-select-positioner[hidden] { display:none; }
.gn-select-popup { box-sizing:border-box; max-height:var(--available-height); overflow-y:auto; overscroll-behavior:contain; padding:6px; border:0; border-radius:var(--gn-form-r-well); background:var(--gn-form-bg); box-shadow:0 0 0 1px var(--gn-form-line), var(--gn-form-shadow); outline:none; }
.gn-select-list { display:flex; flex-direction:column; gap:2px; }
.gn-select-item { box-sizing:border-box; display:flex; align-items:center; justify-content:space-between; gap:8px; min-height:40px; padding:0 14px; border-radius:var(--gn-form-r-pill); color:var(--gn-form-ink); font-size:13px; font-weight:var(--gn-form-strong); cursor:pointer; outline:none; }
.gn-select-item[data-highlighted] { background:var(--gn-form-chip); }
.gn-select-item[data-selected] { background:var(--gn-form-answer); color:var(--gn-form-answer-fg); font-weight:700; }
.gn-select-check { display:flex; flex:none; }
@media (prefers-reduced-motion: reduce) { .gn-select-trigger, .gn-select-icon { transition:none; } }
`;

/**
 * 下拉选择：标签、触发器、列表、提示和错误。底层是 Base UI 的 Select，
 * 键盘选择、读屏（combobox + listbox）、标签与提示的关联都由它处理。
 * 列表浮在触发器下面，可以用 open 固定展开。
 */
export function SelectField({
  tokens: t,
  label,
  options,
  name,
  value,
  defaultValue,
  onValueChange,
  placeholder,
  description,
  error,
  invalid = false,
  disabled = false,
  open,
  onOpenChange,
}: SelectFieldProps) {
  const isInvalid = invalid || Boolean(error);
  return (
    <Field.Root
      className="gn-form-field"
      disabled={disabled}
      invalid={isInvalid}
      name={name}
      style={formVars(t)}
    >
      <style>{`${FORM_STYLES}${SELECT_FIELD_STYLES}`}</style>
      <Select.Root
        defaultValue={defaultValue}
        disabled={disabled}
        items={options}
        modal={false}
        onOpenChange={(next) => onOpenChange?.(next)}
        onValueChange={(next) => {
          if (next !== null) onValueChange?.(next);
        }}
        open={open}
        value={value}
      >
        <Select.Label className="gn-form-label">{label}</Select.Label>
        <Select.Trigger className="gn-select-trigger">
          <Select.Value
            className="gn-select-value"
            placeholder={placeholder ?? m.widget_select_placeholder()}
          />
          <Select.Icon className="gn-select-icon">
            <ChevronDownIcon size={14} />
          </Select.Icon>
        </Select.Trigger>
        <Select.Positioner
          align="start"
          alignItemWithTrigger={false}
          className="gn-select-positioner"
          sideOffset={6}
        >
          <Select.Popup className="gn-select-popup">
            <Select.List className="gn-select-list">
              {options.map((option) => (
                <Select.Item
                  className="gn-select-item"
                  key={option.value}
                  label={option.label}
                  value={option.value}
                >
                  <Select.ItemText>{option.label}</Select.ItemText>
                  <Select.ItemIndicator className="gn-select-check">
                    <CheckIcon size={14} />
                  </Select.ItemIndicator>
                </Select.Item>
              ))}
            </Select.List>
          </Select.Popup>
        </Select.Positioner>
      </Select.Root>
      {error ? (
        <Field.Error className="gn-form-error" match role="alert">
          <AlertIcon size={14} />
          <span>{error}</span>
        </Field.Error>
      ) : (
        description && (
          <Field.Description className="gn-form-hint">
            {description}
          </Field.Description>
        )
      )}
    </Field.Root>
  );
}
